import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';

const ScopeContext = createContext(null);

/**
 * Admins are pinned to their own school; superadmins get a dropdown and
 * the choice sticks in sessionStorage so it survives navigation.
 */
export function SchoolScopeProvider({ children }) {
  const { user } = useAuth();
  const isSuper = user.role === 'superadmin';

  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(() => {
    if (!isSuper) return user.schoolId ?? null;
    const saved = sessionStorage.getItem('cybercub.schoolId');
    return saved ? Number(saved) : null;
  });

  useEffect(() => {
    if (!isSuper) return;
    api
      .adminSchools()
      .then(({ schools: list }) => {
        setSchools(list);
        setSchoolId((current) => current ?? list[0]?.id ?? null);
      })
      .catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    if (isSuper && schoolId) sessionStorage.setItem('cybercub.schoolId', String(schoolId));
  }, [isSuper, schoolId]);

  const value = useMemo(
    () => ({ schoolId, setSchoolId, schools, isSuper }),
    [schoolId, schools, isSuper],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useSchoolScope() {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error('useSchoolScope must be used inside <SchoolScopeProvider>');

  const picker = ctx.isSuper ? (
    <div className="school-picker">
      <label htmlFor="school-scope">Viewing school</label>
      <select
        id="school-scope"
        value={ctx.schoolId ?? ''}
        onChange={(e) => ctx.setSchoolId(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">— choose a school —</option>
        {ctx.schools.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.code}){s.students !== undefined ? ` · ${s.students} students` : ''}
          </option>
        ))}
      </select>
    </div>
  ) : null;

  return { ...ctx, picker };
}
